import { useMemo } from 'react';
import { LatLng } from 'leaflet';
import { useRouteBuilderState } from '@lp/features/RoutePlanner/store';
import { AircraftMarkerListItem, AircraftStatusEnum, MapFilterType } from '@lp/features/RoutePlanner/types';
import { aircraftStatusWeight, getAircraftItem } from '@lp/features/RoutePlanner/utils';
import { parseZonedDateTime } from '@internationalized/date';
import { MAX_LEG_FLIGHT_HOURS } from '@lp/features/RoutePlanner/config';
import { useAircraftAvailability } from '@/queries/resource/availability';
import { useSearchableList } from '@/hooks/useSearchableList';
import { calculateFlightDurationMs } from '@/utils/route';

type UseAircraftListType = {
  list: AircraftMarkerListItem[];
  isLoading: boolean;
  searchValue: string;
  onAircraftSearch: (text: string) => void;
};

type UseAircraftListProps = {
  caseId: string;
  departureLocation: LatLng | null;
  startTime?: string;
  endTime?: string;
};

export const useAircraftList = ({
  caseId,
  departureLocation,
  startTime,
  endTime,
}: UseAircraftListProps): UseAircraftListType => {
  const filters = useRouteBuilderState.use.filters();

  const { data: aircrafts, isFetching: isAircraftsFetching } = useAircraftAvailability({
    caseId,
    reserved: true,
  });

  const aircraftsList = useMemo(() => {
    if (!aircrafts?.length || !filters) return [];

    const dateFrom = startTime ? parseZonedDateTime(startTime).toAbsoluteString() : filters?.[MapFilterType.from];
    const defaultDateTo = startTime
      ? parseZonedDateTime(startTime).add({ milliseconds: MAX_LEG_FLIGHT_HOURS }).toAbsoluteString()
      : filters?.[MapFilterType.to];
    const dateTo = endTime ? parseZonedDateTime(endTime).toAbsoluteString() : defaultDateTo;

    const unavailableAircrafts = aircrafts
      .filter((a) => !a.predictedAvailabilities.length)
      .map((i) => {
        const aircraftItem = getAircraftItem({ aircraft: i });

        return {
          ...aircraftItem,
          status: AircraftStatusEnum.unavailable,
          flightTimeMs: -1,
        };
      });

    const availableAircraftsList = aircrafts
      .filter((a) => !!a.predictedAvailabilities.length)
      .map((i) => {
        const aircraftItem = getAircraftItem({
          aircraft: i,
          filters: { [MapFilterType.from]: dateFrom, [MapFilterType.to]: dateTo },
          shouldDateIncludes: true,
        });

        const currentAvailability = aircraftItem.availabilities[0];

        const flightTimeMs =
          departureLocation && currentAvailability
            ? calculateFlightDurationMs(
                departureLocation,
                new LatLng(currentAvailability.airport.latitude, currentAvailability.airport.longitude),
              )
            : 0;
        return {
          ...aircraftItem,
          flightTimeMs,
        };
      })
      .filter((a) => a.availabilities.length)
      .sort((a, b) => {
        const statusComparison = aircraftStatusWeight[a.status] - aircraftStatusWeight[b.status];

        if (statusComparison !== 0) {
          return statusComparison;
        }

        if (!a.flightTimeMs) return 1;
        if (!b.flightTimeMs) return -1;

        return a.flightTimeMs - b.flightTimeMs;
      });

    return [...availableAircraftsList, ...unavailableAircrafts];
  }, [aircrafts, filters, startTime, endTime, departureLocation]);

  const { list, setSearchValue: onAircraftSearch, searchValue } = useSearchableList(aircraftsList, ['registration']);

  return {
    isLoading: isAircraftsFetching,
    list,
    onAircraftSearch,
    searchValue,
  };
};
